import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Pencil, Trash2, Save, X, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Fields displayed and editable per kind. Order matters — first is key.
const FIELDS = {
  'invoice-pi': [
    { key: 'invoice_num',    label: 'Sl #',        readonly: true, width: 60 },
    { key: 'invoice_no',     label: 'Invoice No',  width: 130 },
    { key: 'date',           label: 'Date',        width: 110 },
    { key: 'consignee',      label: 'Consignee',   width: 200 },
    { key: 'country',        label: 'Country',     width: 110 },
    { key: 'invoice_amount', label: 'Amount',      width: 140 },
    { key: 'fob_value',      label: 'FOB',         width: 140 },
    { key: 'net_weight',     label: 'Net Wt',      width: 100 },
    { key: 'container',      label: 'Container',   width: 130 },
  ],
  'solar-project': [
    { key: 'SlNo',           label: 'Sl #',        readonly: true, width: 60 },
    { key: 'Vendor',         label: 'Vendor',      width: 220 },
    { key: 'InvoiceNo',      label: 'Invoice No',  width: 140 },
    { key: 'Date',           label: 'Date',        width: 110 },
    { key: 'Category',       label: 'Category',    width: 140 },
    { key: 'InvoiceAmount',  label: 'Invoice Amt', width: 130, numeric: true },
    { key: 'ActualSIL',      label: 'SIL Paid',    width: 120, numeric: true },
    { key: 'ActualSBI',      label: 'SBI Paid',    width: 120, numeric: true },
  ],
};

// Key used in URL for PUT/DELETE
const KEY_FIELD = { 'invoice-pi': 'invoice_num', 'solar-project': 'SlNo' };

function EditDialog({ open, onOpenChange, kind, row, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(row || {}); }, [row]);

  if (!row) return null;
  const fields = FIELDS[kind];
  const keyField = KEY_FIELD[kind];
  const rowKey = row[keyField];

  const handleSave = async () => {
    // Build only changed fields
    const changes = {};
    for (const f of fields) {
      if (f.readonly) continue;
      const before = row[f.key];
      const after = draft[f.key];
      if (String(before ?? '') !== String(after ?? '')) {
        changes[f.key] = f.numeric && after !== '' ? Number(after) : after;
      }
    }
    if (Object.keys(changes).length === 0) {
      toast('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/rows/${kind}/${rowKey}`, changes, { withCredentials: true });
      toast.success('Row saved');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-dash-panel border-dash-line" data-testid={`edit-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>Edit row · {kind === 'invoice-pi' ? `Invoice ${row.invoice_no}` : `Sl No ${rowKey}`}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          {fields.map(f => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={`edit-${f.key}`} className="text-xs text-dash-mut">{f.label}</Label>
              <Input
                id={`edit-${f.key}`}
                value={draft[f.key] ?? ''}
                readOnly={f.readonly}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                disabled={f.readonly}
                data-testid={`edit-field-${f.key}`}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid={`save-row-${kind}`}>
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RowEditor({ kind, onChange }) {
  const fields = FIELDS[kind];
  const keyField = KEY_FIELD[kind];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/rows/${kind}`, { withCredentials: true });
      setRows(data.rows || []);
    } catch (e) {
      toast.error('Failed to load rows');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kind]);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter(r =>
      fields.some(f => String(r[f.key] ?? '').toLowerCase().includes(q))
    );
  }, [rows, query, fields]);

  const handleDelete = async (row) => {
    const rowKey = row[keyField];
    if (!window.confirm(`Delete ${kind === 'invoice-pi' ? `invoice ${row.invoice_no}` : `Sl No ${rowKey}`}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/rows/${kind}/${rowKey}`, { withCredentials: true });
      toast.success('Row deleted');
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-3" data-testid={`row-editor-${kind}`}>
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dash-mut" />
          <Input
            placeholder={`Search ${rows.length} rows…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid={`row-search-${kind}`}
          />
        </div>
        <span className="text-xs text-dash-mut">{filtered.length} shown</span>
      </div>

      <div className="overflow-auto max-h-[560px] rounded border border-dash-line">
        <table className="min-w-full text-xs">
          <thead className="bg-dash-panel2 sticky top-0 z-10">
            <tr>
              {fields.map(f => (
                <th key={f.key} className="px-3 py-2 text-left text-dash-mut font-normal whitespace-nowrap" style={{ width: f.width }}>{f.label}</th>
              ))}
              <th className="px-3 py-2 text-right text-dash-mut font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={fields.length + 1} className="text-center text-dash-mut py-8">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={fields.length + 1} className="text-center text-dash-mut py-8">No rows.</td></tr>
            )}
            {!loading && filtered.map(row => (
              <tr key={row[keyField]} className="border-t border-dash-line/40 hover:bg-dash-panel2/40">
                {fields.map(f => (
                  <td key={f.key} className="px-3 py-1.5 text-dash-ink truncate max-w-[220px]" title={String(row[f.key] ?? '')}>
                    {row[f.key] === null || row[f.key] === undefined ? '—' : String(row[f.key])}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(row)} data-testid={`edit-row-${row[keyField]}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(row)} data-testid={`delete-row-${row[keyField]}`} className="text-red-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EditDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        kind={kind}
        row={editing}
        onSaved={() => { load(); onChange?.(); }}
      />
    </div>
  );
}
